import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    Ship,
    Package,
    ArrowRight,
    AlertCircle,
    Sparkles,
    Truck,
    List,
    LayoutGrid,
    Plus,
    Activity,
    BookmarkPlus,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
    getImportBookingList,
    deleteImportBookingDetails,
    getImportBookingDetails,
    deleteImportBookingList,
} from '@/redux/ImportBooking/actions';
import { callLoader } from "@/redux/Loader/actions";
import { toast } from "sonner";
import Modal from "@/components/ui/Modal";
import FileUpload from "@/components/FileUpload/FileUpload";
import UploadNotes from "@/components/NotesUpload/NotesUpload";
import Swal from "sweetalert2";
import FilterBar from "@/components/shipment/FilterBar";
import ShipmentCard from "@/components/shipment/ShipmentCard";
import Pagination from "@/pages/PortPlace/Pagination";
import { showToast } from "@/redux/Toast/actions";
import AIInsightsModal from "@/components/OceanImport/AIInsightsModal";
import BookingCancelModal from "@/components/Modal/BookingCancelModal";
import AIInsightCard from "@/components/shipment/AIInsightCard";
import PrintDocumentsSelectionModal from "@/components/PrintDocuments/PrintDocumentsSelectionModal";
import { ReportsNavButton } from "@/components/reports";

const appendBlLetterhead = (url, blPrintOnLetterhead) => {
    if (!blPrintOnLetterhead) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}blLetterhead=1`;
};


// ─── Animated typewriter title ────────────────────────────────────────────────
function AnimatedTitle() {
    const { t } = useTranslation();
    const TITLE = t("oceanImport.importBooking.list.title");
    return (
        <motion.h1
            initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="text-[18px] sm:text-[20px] font-bold tracking-tight leading-tight
                       text-gray-900 dark:text-white"
        >
            <motion.span
                className="inline-block overflow-hidden whitespace-nowrap align-bottom"
                initial={{ width: "0ch" }}
                animate={{ width: ["0ch", `${TITLE.length}ch`, `${TITLE.length}ch`, "0ch"] }}
                transition={{
                    duration: 4.8,
                    times: [0, 0.22, 0.9, 1],
                    ease: ["linear", "linear", "easeInOut"],
                    repeat: Infinity,
                }}
            >
                {TITLE}
            </motion.span>
        </motion.h1>
    );
}

// ─── View Toggle ──────────────────────────────────────────────────────────────
function ViewToggle({ view, setView }) {
    const { t } = useTranslation();
    const base = "w-9 h-9 flex items-center justify-center rounded-md transition-colors";
    const active = "bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400";
    const inactive = "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100";

    return (
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700
                        bg-white dark:bg-gray-900 p-0.5">
            <button onClick={() => setView("table")} title={t("oceanImport.common.listView")} className={`${base} ${view === "table" ? active : inactive}`}>
                <List className="w-4 h-4" />
            </button>
            <button onClick={() => setView("card")} title={t("oceanImport.common.gridView")} className={`${base} ${view === "card" ? active : inactive}`}>
                <LayoutGrid className="w-4 h-4" />
            </button>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const ImportBookingList = ({ onCreateNew, onViewShipment, onPrintShipment }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { importbookingList = [], importpagination, importsummary: summary } = useSelector((s) => s.importBooking);
    const { fileManagerList = [], MasterCompanyDetails = [] } = useSelector((state) => state.common);

    const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
    const userName = authUser?.username;
    const companyId = authUser?.companyId;

    const [showNote, setshowNote] = useState(false);
    const [fileUploadVisible, setFileUploadVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [payload, setPayload] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAIInsights, setShowAIInsights] = useState(false);
    const [expand, setExpand] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [view, setView] = useState("table");
    const [deleteData, setDeleteData] = useState(null);
    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [selectedPrintBooking, setSelectedPrintBooking] = useState(null);
    const divRef = useRef(null);
    const [width, setWidth] = useState(0);


    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchBookings = async (page = currentPage, filter = null) => {
        dispatch(callLoader(1));
        setIsLoading(true);
        try {
            const requestPayload = {
                CompanyID: companyId || 0,
                IPageNo: page || 1,
                IPageRecords: filter?.pageSize || recordsPerPage,

                SearchKeyword: filter?.search || "",
                FilterType: filter?.status || "ALL",

                POLCode: filter?.pol || "",
                PODCode: filter?.pod || "",
                shortby: filter?.sortBy || "",
                CarrierSCAC: filter?.carrier === "all" ? "" : filter?.carrier || "",

                FromDate: filter?.dateFrom || "",
                ToDate: filter?.dateTo || "",
            };

            console.log("API Payload:", requestPayload);

            await dispatch(getImportBookingList(requestPayload));
            setCurrentPage(page);
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error(t("failedLoadShipments"));
        } finally {
            dispatch(callLoader(0));
            setIsLoading(false);
        }
    };


    useEffect(() => {
        fetchBookings(1);
        console.log("hey", importbookingList)
    }, []);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleFilter = (f) => { setPayload(f); fetchBookings(1, f); };
    const handlePageSize = (size) => { setRecordsPerPage(size); fetchBookings(1, { pageSize: size }); setCurrentPage(1); };
    // Make uniqueCarriers an array of unique { CarrierSCAC, CarrierName } combinations
    const uniqueCarriers = [
        ...new Map(
            importbookingList
                .filter(s => s.CarrierSCAC && s.CarrierName)
                .map(s => [`${s.CarrierSCAC}|${s.CarrierName}`, { CarrierSCAC: s.CarrierSCAC, CarrierName: s.CarrierName }])
        ).values()
    ];
    // console.log("uniqueCarriers", uniqueCarriers)
    const importPrintDocuments = useMemo(() => {
        if (!selectedPrintBooking?.BolID || Number(selectedPrintBooking.BolID) <= 0) return [];
        return [
            {
                id: "arrival_notice",
                name: "ARRIVAL NOTICE (FREIGHTED)",
                remarks: `Arrival Notice — File: ${selectedPrintBooking.FileRefNumber || "—"}`,
                defaultSelected: true,
            },
            {
                id: "arrival_notice_non_freighted",
                name: "ARRIVAL NOTICE (NON FREIGHTED)",
                remarks: `Arrival Notice (Non Freighted) — File: ${selectedPrintBooking.FileRefNumber || "—"}`,
                defaultSelected: false,
            },
            {
                id: "release_order",
                name: "RELEASE ORDER",
                remarks: `Release Order — File: ${selectedPrintBooking.FileRefNumber || "—"}`,
                defaultSelected: false,
            },
            {
                id: "hbl_original_freighted",
                name: "HBL ORIGINAL FREIGHTED",
                remarks: selectedPrintBooking.HBLNumber ? `HBL: ${selectedPrintBooking.HBLNumber}` : "",
                defaultSelected: false,
            },
            {
                id: "isf_worksheet",
                name: "ISF WORKSHEET",
                remarks: selectedPrintBooking.FileRefNumber ? `ISF — File: ${selectedPrintBooking.FileRefNumber}` : "Importer Security Filing summary",
                defaultSelected: false,
            },
            {
                id: "delivery_order",
                name: "DELIVERY ORDER",
                remarks: `Delivery Order - File: ${selectedPrintBooking.FileRefNumber || "—"}`,
                defaultSelected: false,
            },
            {
                id: "rate_quote",
                name: "RATE QUOTE",
                remarks:
                    selectedPrintBooking.RateQuoteNumber
                        ? `Quote: ${selectedPrintBooking.RateQuoteNumber}`
                        : Number(selectedPrintBooking.RateQuoteID) > 0
                            ? `Quote ID: ${selectedPrintBooking.RateQuoteID}`
                            : "No rate quote linked",
                highlightRow: true,
                defaultSelected: false,
            },
        ];
    }, [selectedPrintBooking]);

    const handleImportPrintDocuments = useCallback(({ items, blPrintOnLetterhead }) => {
        const bolId = selectedPrintBooking?.BolID;
        if (!bolId || Number(bolId) <= 0) {
            toast.error("Booking ID missing.");
            return;
        }

        const fileRef = selectedPrintBooking?.FileRefNumber || "";
        let delayMs = 0;
        let scheduled = 0;
        const openStaggered = (url) => {
            const finalUrl = appendBlLetterhead(url, blPrintOnLetterhead);
            scheduled += 1;
            window.setTimeout(() => {
                window.open(finalUrl, "_blank", "noopener,noreferrer");
            }, delayMs);
            delayMs += 350;
        };

        for (const item of items) {
            let path = null;
            switch (item.id) {
                case "arrival_notice":
                    if (MasterCompanyDetails?.[0]?.CompanyId === 2) {
                        path = `/import-booking/PrintLockerFreight/${btoa(String(bolId))}`;
                    } else {
                        path = `/import-booking/Print/${btoa(String(bolId))}`;
                    }
                    break;
                case "arrival_notice_non_freighted":
                    if (MasterCompanyDetails?.[0]?.CompanyId === 2) {
                        path = `/import-booking/PrintLockerFreight/${btoa(String(bolId))}`;
                    } else {
                        path = `/import-booking/PrintNonFreight/${btoa(String(bolId))}`;
                    }
                    break;
                case "release_order":
                    path = `/release-order/print/${btoa(String(bolId))}?fileRefNumber=${encodeURIComponent(fileRef)}`;
                    break;
                case "hbl_original_freighted":
                    path = `/import-hbl-print/${btoa(String(bolId))}`;
                    break;
                case "isf_worksheet":
                    path = `/import-isf-print/${btoa(String(bolId))}`;
                    break;
                case "delivery_order":
                    path = `/delivery-order/print/${btoa(String(bolId))}`;
                    break;
                case "rate_quote": {
                    const rq = selectedPrintBooking?.RateQuoteID;
                    if (!rq || Number(rq) <= 0) {
                        toast.error("No import rate quote linked — link a quote first.");
                        continue;
                    }
                    path = `/import-rate-quote/print/${btoa(String(rq))}`;
                    break;
                }
                default:
                    toast.error(`Unknown document type: ${item.name}`);
                    continue;
            }

            const copies = Math.max(1, Number(item.copies) || 1);
            for (let c = 0; c < copies; c++) openStaggered(path);
        }

        if (scheduled > 0) {
            toast.success(t("oceanImport.importBooking.list.printOpening"));
            setPrintModalOpen(false);
            setSelectedPrintBooking(null);
        } else {
            toast.error("No print views were opened. Check linked rate quote or try again.");
        }
    }, [selectedPrintBooking, MasterCompanyDetails]);

    const handlePrint = (shipment) => {
        setSelectedPrintBooking(shipment);
        setPrintModalOpen(true);
    };

    const handleEmail = (shipment) => {
        navigate(`/mail/compose?id=${shipment?.id}&type=import-Booking`);
    };

    const changePage = (page) => {
        if (currentPage === page) return;
        setCurrentPage(page);
        fetchBookings(page, payload);
    };

    const openImportBookingInNewTab = useCallback((id) => {
        const path = id ? `/import-booking/edit/${id}` : "/import-booking/new";
        window.open(
            `${window.location.origin}${path}`,
            "_blank",
            "noopener,noreferrer",
        );
    }, []);

    const handleViewShipment = onViewShipment ?? openImportBookingInNewTab;

    const handleDeleteBooking = async (id) => {
        const result = await Swal.fire({
            title: t("areYouSure"),
            text: "This booking will be inactivated.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        });
        if (!result.isConfirmed) return;
        try {
            await deleteImportBookingDetails({ BookingId: id, ModifiedBy: userName });
            await fetchBookings(1, payload);
            toast.success(t("bookingInactivatedSuccessfully"));
        } catch (error) { toast.error(error); }
    };

    const handleOpenNotes = (shipment) => { setSelectedOrderId(shipment); setshowNote(true); };
    const handleOpenFileUpload = (shipment) => { setSelectedOrderId(shipment); setFileUploadVisible(true); };

    const _setRestoreDataPopup = (data) => setDeleteData(data?.BolID ?? data);

    const _setRestoreData = async (remark) => {
        await deleteImportBookingList({ BookingId: deleteData, ModifyBy: userName, BookingCancelRemarks: remark })
            .then(() => fetchBookings(currentPage))
            .catch((error) => dispatch(showToast('error', error?.message || t("failedRestoreShipment"))))
            .finally(() => setDeleteData(null));
    };

    // ── KPI cards ─────────────────────────────────────────────────────────────
    const insightCards = useMemo(() => [
        { icon: Package, title: t("totalShipments"), value: summary?.total ?? 0, trend: "+12%", trendUp: true, accentKey: "blue" },
        { icon: Activity, title: t("active"), value: summary?.active ?? 0, trend: t("onTrack"), trendUp: true, accentKey: "emerald" },
        { icon: Truck, title: t("inTransit"), value: summary?.intransit ?? 0, trend: "3 arriving", trendUp: true, accentKey: "amber" },
        { icon: AlertCircle, title: t("needsAttention"), value: summary?.needattension ?? 0, trend: t("reviewRequired"), trendUp: false, accentKey: "red" },
    ], [summary, t]);


    const cardProps = (shipment, index) => {
        const normalized = {
            ...shipment,
            id: shipment.BolID,
            fileNumber: shipment.CarrierBookingNo,
            carrier: shipment.CarrierName,
            customerName: shipment.BookingPartyName || shipment.ImporterName || shipment.Consignee,
            shipper: shipment.Shipper,
            consignee: shipment.Consignee,
            portOfLoading: shipment.PortOfLoading,
            portOfDischarge: shipment.PortOfDischarge,
            portOfLoadingCode: shipment.PortOfLoadingCode,
            portOfDischargeCode: shipment.PortOfDischargeCode,
            shippingType: shipment.ShippingTerm,
            vessel: shipment.VesselName,
            carrierBookingNumber: shipment.FileRefNumber,
            createdBy: shipment.CreatedBy,
            createdOn: shipment.CreatedOn,
            modifiedBy: shipment.ModifiedBy,
            etaAtPod: shipment.ETAPOD,
            etsAtPol: shipment.EtdAtPol,
            isActive: shipment.IsActive,
            fileManager: fileManagerList.find((fm) => fm.id === shipment.FileManager)?.filemanager || "",
        };
        return {
            key: normalized.id || index,
            shipment: normalized,
            isImport: true,
            onView: handleViewShipment,
            onPrint: handlePrint,
            onEmail: handleEmail,
            onDelete: handleDeleteBooking,
            onOpenNotes: handleOpenNotes,
            onOpenFileUpload: handleOpenFileUpload,
            expand,
            setExpand,
            setRestoreDeletePopup: _setRestoreDataPopup,
        };
    };


    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-full space-y-3 p-4">

            {/* ── HEADER CARD ───────────────────────────────────────────────── */}
            <div className="rounded-2xl overflow-hidden
                            border border-gray-200 dark:border-gray-800
                            bg-white dark:bg-gray-900
                            shadow-sm dark:shadow-gray-900/40">

                {/* Top bar */}
                <div className="px-4 sm:px-5 py-4
                                flex flex-wrap items-center justify-between gap-3
                                border-b border-gray-100 dark:border-gray-800">

                    <div className="flex items-center gap-3 min-w-0">
                        <div className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                                        bg-blue-50 dark:bg-blue-900/40
                                        ring-1 ring-blue-100 dark:ring-blue-800
                                        flex items-center justify-center">
                            <Ship className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                            <AnimatedTitle />
                            <p className="mt-0.5 text-[12px] text-gray-500 dark:text-gray-400
                                          hidden sm:block truncate max-w-sm lg:max-w-lg">
                                {t('shipmentManagementDesc')}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <ReportsNavButton moduleId="ocean-fcl" mode="import" />
                        <button
                            onClick={() => navigate('/import-rate-quote')}
                            className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                                       bg-white dark:bg-gray-800
                                       border border-gray-200 dark:border-gray-700
                                       text-gray-700 dark:text-gray-300
                                       hover:bg-gray-50 hover:border-gray-300
                                       dark:hover:bg-gray-700/70 dark:hover:border-gray-600
                                       text-[12.5px] font-semibold transition-colors"
                        >
                            <BookmarkPlus className="w-3.5 h-3.5" />
                            {/* <span className="hidden sm:inline">{t('importRateQuote')}</span> */}
                            <span>{t("oceanImport.importBooking.list.importRateQuote")}</span>
                        </button>

                        <button
                            onClick={() => openImportBookingInNewTab()}
                            className="inline-flex items-center gap-1.5 h-9 px-3 sm:px-3.5 rounded-lg
                                       bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                                       dark:bg-blue-500 dark:hover:bg-blue-600
                                       text-white text-[12.5px] font-semibold
                                       shadow-[0_4px_14px_-4px_rgba(37,99,235,0.45)]
                                       dark:shadow-[0_4px_14px_-4px_rgba(59,130,246,0.35)]
                                       transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span className="sm:inline">{t("oceanImport.importBooking.list.newShipment")}</span>

                        </button>

                        <ViewToggle view={view} setView={setView} />
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="px-4 sm:px-5 pt-4 pb-3
                                grid grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
                    {insightCards.map((card) => (
                        <AIInsightCard key={card.title} {...card} />
                    ))}
                </div>

                {/* AI Insight Banner */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25 }}
                    className="mx-4 sm:mx-5 mb-4 relative overflow-hidden
                               flex flex-col sm:flex-row items-start sm:items-center gap-3
                               rounded-xl px-4 py-3
                               bg-gradient-to-r from-violet-50 to-indigo-50
                               dark:from-violet-950/50 dark:to-indigo-950/50
                               border border-violet-100 dark:border-violet-900/60"
                >
                    <span
                        aria-hidden
                        className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl
                                   bg-gradient-to-b from-violet-500 via-fuchsia-500 to-pink-500"
                    />
                    <div className="shrink-0 w-9 h-9 rounded-lg ml-1
                                    bg-gradient-to-br from-violet-500 to-indigo-500
                                    flex items-center justify-center
                                    shadow-[0_4px_12px_-2px_rgba(139,92,246,0.4)]">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.16em]
                                        text-violet-600 dark:text-violet-400">
                            {t('aiInsight')}
                        </div>
                        <p className="mt-0.5 text-[12.5px] leading-relaxed
                                      text-gray-700 dark:text-gray-300">
                            {t('aiInsightPrediction')}
                        </p>
                    </div>
                    <button
                        onClick={() => setShowAIInsights(true)}
                        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                                   text-[12px] font-semibold
                                   text-violet-700 dark:text-violet-300
                                   bg-violet-100 dark:bg-violet-900/50
                                   hover:bg-violet-200 dark:hover:bg-violet-900
                                   ring-1 ring-violet-200 dark:ring-violet-800
                                   transition-colors"
                        data-testid="view-ai-insights-btn"
                    >
                        {t('viewDetails')} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </motion.div>
            </div>

            {/* ── Filter Bar ────────────────────────────────────────────────── */}
            <FilterBar
                uniqueCarriers={uniqueCarriers}
                onFilter={handleFilter}
                stats={{
                    total: importpagination?.totalRecord,
                    showRecord: importpagination?.currentRecord || 0,
                }}
            />

            {/* ── Content ───────────────────────────────────────────────────── */}
            <div >
                {importbookingList.length === 0 ? (
                    /* Empty state */
                    <div className="text-center py-20
                                    bg-white dark:bg-gray-900
                                    rounded-2xl border border-gray-200 dark:border-gray-800">
                        <Ship className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-600 dark:text-gray-300 font-semibold text-lg">
                            {t('noShipmentsFound')}
                        </p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                            {t('createBookingStart')}
                        </p>
                        <button
                            onClick={() => openImportBookingInNewTab()}
                            className="mt-4 px-5 py-2.5 bg-blue-600 text-white rounded-xl
                                       text-sm font-semibold hover:bg-blue-700 transition-colors"
                        >
                            + {t('createBooking')}
                        </button>
                    </div>
                ) : view !== "table" ? (
                    /* Card grid */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {importbookingList.map((shipment, i) => (
                            <ShipmentCard {...cardProps(shipment, i)} />
                        ))}
                    </div>
                ) : (
                    /* List view */
                    <div className="space-y-4 overflow-auto rounded-xl mt-4">
                        {importbookingList.map((shipment, i) => (
                            <ShipmentCard {...cardProps(shipment, i)} />
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {!isLoading && importpagination?.totalPage > 0 && (
                    <Pagination
                        className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8 mb-4 bg-slate-50 dark:bg-slate-800/50 mt-8"
                        currentPage={currentPage}
                        recordsPerPage={recordsPerPage}
                        setRecordsPerPage={handlePageSize}
                        onPrevious={changePage}
                        onNext={changePage}
                        totalRecord={importpagination?.totalRecord}
                        handleGoto={changePage}
                        title={t("oceanImport.importBooking.list.shipments")}
                    />
                )}
            </div>

            {/* ── Modals ────────────────────────────────────────────────────── */}
            <AnimatePresence>
                {showAIInsights && (
                    <AIInsightsModal
                        isOpen={showAIInsights}
                        onClose={() => setShowAIInsights(false)}
                        shipments={importbookingList}
                        insightCards={insightCards}
                    />
                )}
            </AnimatePresence>

            <Modal
                isOpen={fileUploadVisible}
                onClose={() => { setFileUploadVisible(false); setSelectedOrderId(null); }}
            >
                <FileUpload
                    orderId={selectedOrderId?.BolID}
                    orderNum={selectedOrderId?.FileRefNumber}
                    type="ImportBooking"
                />
            </Modal>

            <Modal
                isOpen={showNote}
                onClose={() => { setshowNote(false); setSelectedOrderId(null); }}
            >
                <UploadNotes
                    BookingID={selectedOrderId?.BolID || ""}
                    RefNumber={selectedOrderId?.FileRefNumber}
                    type="ImportBooking"
                />
            </Modal>

            {deleteData && (
                <BookingCancelModal
                    open
                    onClose={() => setDeleteData(null)}
                    onDelete={_setRestoreData}
                />
            )}

            <PrintDocumentsSelectionModal
                isOpen={printModalOpen}
                onClose={() => {
                    setPrintModalOpen(false);
                    setSelectedPrintBooking(null);
                }}
                documents={importPrintDocuments}
                onPrint={handleImportPrintDocuments}
                title={t("oceanImport.importBooking.list.printDocumentsTitle")}
            />
        </div>
    );
};

export default ImportBookingList;